import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import { BASE_URL } from "../api";
import { getApiHeaders, getFullMediaUrl } from "../utils/helpers";
import MessageToster from "../component/MessageToster";
import { useHandleError } from "../hooks/useHandleError";
import copy from "copy-to-clipboard";
import CommonTable from "../component/CommonTable";
import { useUser } from "../context/UserContext";

const DEFAULT_COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "United Arab Emirates",
  "Canada",
  "Australia",
  "Singapore",
  "Germany",
  "France",
  "South Africa",
  "Malaysia",
  "Saudi Arabia",
];

export default function BankDetails() {
  const handleError = useHandleError();
  const { userData, fetchUserData } = useUser();
  const fileInputRef = useRef(null);
  const countryRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [docModalData, setDocModalData] = useState(null);
  const [countries, setCountries] = useState(DEFAULT_COUNTRIES);

  // Check if bank details are already added (one time only)
  const isBankAdded = Boolean(
    userData?.isBankDetailsAdded === true ||
    userData?.isBankDetailsAdded === "true" ||
    userData?.isBankDetailsAdded === 1 ||
    userData?.is_bank_details_added === true ||
    bankList.length > 0
  );

  // Searchable country dropdown states
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");

  const initialFormState = {
    account_holder_name: "",
    account_no: "",
    ifsc_swift_code: "",
    iban_no: "",
    bank_name: "",
    bank_address: "",
    country: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [bookBankFile, setBookBankFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBankDetails();
    fetchCountryData();
    if (fetchUserData) {
      fetchUserData();
    }
  }, []);

  // Control form visibility based on isBankAdded
  useEffect(() => {
    if (isBankAdded) {
      setShowAddForm(false);
    } else if (!loading && bankList.length === 0) {
      setShowAddForm(true);
    }
  }, [isBankAdded, bankList.length, loading]);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setDocModalData(null);
      }
    };
    if (docModalData) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [docModalData]);

  // Close country dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch country list from backend
  const fetchCountryData = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/account/country_data`, {
        headers: getApiHeaders(),
        timeout: 5000,
      });

      const raw = res.data?.country_data || res.data?.country_name || res.data?.data || [];
      if (Array.isArray(raw) && raw.length > 0) {
        const list = raw
          .map((c) => (typeof c === "string" ? c : c.country_name || c.name || ""))
          .filter(Boolean);
        if (list.length > 0) {
          setCountries(list);
        }
      }
    } catch (err) {
      console.log("Country list fallback used:", err?.message);
    }
  };

  // Fetch saved bank details
  const fetchBankDetails = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/account/get_bank_details`, {
        headers: getApiHeaders(),
        timeout: 5000,
      });

      if (res.data?.code === 200 || res.data?.status === true || res.data?.status === "success" || res.status === 200) {
        const rawData = res.data?.data || res.data?.bank_details || res.data;
        let list = [];

        if (Array.isArray(rawData)) {
          list = rawData.flat();
        } else if (rawData && typeof rawData === "object") {
          if (rawData.account_no || rawData.account_number || rawData.bank_name || rawData.account_holder_name) {
            list = [rawData];
          }
        }

        setBankList(list);
        if (list.length > 0) {
          setShowAddForm(false);
        }
      } else {
        setBankList([]);
      }
    } catch (error) {
      console.log("get_bank_details:", error?.message);
      setBankList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchBankDetails(),
        fetchUserData ? fetchUserData() : Promise.resolve(),
      ]);
      MessageToster("success", "Bank details refreshed");
    } catch (err) {
      console.log("Refresh error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;

    if (name === "ifsc_swift_code") {
      formatted = value.toUpperCase();
    } else if (name === "account_no") {
      formatted = value.replace(/\D/g, ""); // Only positive digits (0-9)
    }

    setFormData((prev) => ({
      ...prev,
      [name]: formatted,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          book_bank_image: "File size must be under 10MB",
        }));
        return;
      }

      setBookBankFile(file);
      setErrors((prev) => ({ ...prev, book_bank_image: "" }));

      if (file.type.startsWith("image/")) {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview("");
      }
    }
  };

  const clearFile = () => {
    setBookBankFile(null);
    setFilePreview("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.account_holder_name.trim()) {
      newErrors.account_holder_name = "Account holder name is required";
    }

    if (!formData.account_no.trim()) {
      newErrors.account_no = "Account number is required";
    } else if (!/^\d+$/.test(formData.account_no.trim())) {
      newErrors.account_no = "Account number must contain positive digits only";
    }

    if (!formData.bank_name.trim()) {
      newErrors.bank_name = "Bank name is required";
    }

    if (!formData.bank_address.trim()) {
      newErrors.bank_address = "Bank address is required";
    }

    if (!formData.country.trim()) {
      newErrors.country = "Country is required";
    }

    if (!bookBankFile) {
      newErrors.book_bank_image = "Passbook / Bank document image is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitLoading(true);

    try {
      const formPayload = new FormData();
      formPayload.append("account_holder_name", formData.account_holder_name.trim());
      formPayload.append("account_no", formData.account_no.trim());
      formPayload.append("ifsc_swift_code", formData.ifsc_swift_code.trim());
      formPayload.append("iban_no", formData.iban_no.trim());
      formPayload.append("bank_name", formData.bank_name.trim());
      formPayload.append("bank_address", formData.bank_address.trim());
      formPayload.append("country", formData.country.trim());
      formPayload.append("book_bank_image", bookBankFile);

      const res = await axios.post(`${BASE_URL}/account/add_bank_details`, formPayload, {
        headers: getApiHeaders(true),
        timeout: 15000,
      });

      if (
        res.data?.code === 200 ||
        res.data?.status === true ||
        res.data?.status === "success" ||
        res.status === 200 ||
        res.status === 201
      ) {
        MessageToster("success", res.data?.message || "Bank details saved successfully!");
        setFormData(initialFormState);
        clearFile();
        setShowAddForm(false);
        // Refresh bank details list and sync user data context for isBankDetailsAdded
        await fetchBankDetails();
        if (fetchUserData) {
          await fetchUserData();
        }
      } else {
        MessageToster("error", res.data?.message || res.data?.error || "Failed to save bank details");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const copyText = (text, label = "Text") => {
    if (!text) return;
    copy(text);
    MessageToster("success", `${label} copied to clipboard!`);
  };

  const maskAccountNumber = (num) => {
    if (!num) return "•••• •••• ••••";
    const str = String(num);
    if (str.length <= 4) return str;
    return `•••• •••• •••• ${str.slice(-4)}`;
  };

  const tableColumns = [
    {
      header: "#",
      className: "align-middle text-center",
      headerClassName: "text-center",
      render: (_, index) => index + 1,
    },
    {
      header: "Bank Name",
      className: "align-middle fw-medium",
      render: (row) => (
        <div className="d-flex align-items-center">
          <div className="avatar-xs me-2">
            <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-14">
              <i className="bx bx-buildings" />
            </span>
          </div>
          <div>
            <h6 className="font-size-14 mb-0">{row.bank_name || "-"}</h6>
            <small className="text-muted">{row.bank_address || "-"}</small>
          </div>
        </div>
      ),
    },
    {
      header: "Account Holder",
      className: "align-middle",
      render: (row) => row.account_holder_name || row.holder_name || "-",
    },
    {
      header: "Account Number",
      className: "align-middle",
      render: (row) => {
        const acc = row.account_no || row.account_number || "-";
        return (
          <div className="d-flex align-items-center gap-2">
            <span className="font-monospace fw-bold">{acc}</span>
            {acc !== "-" && (
              <button
                type="button"
                className="btn btn-sm btn-soft-secondary p-1"
                title="Copy Account Number"
                onClick={() => copyText(acc, "Account Number")}
              >
                <i className="bx bx-copy" />
              </button>
            )}
          </div>
        );
      },
    },
    {
      header: "IFSC / SWIFT",
      className: "align-middle",
      render: (row) => {
        const code = row.ifsc_swift_code || row.ifsc_code || row.swift_code || "-";
        return code !== "-" ? (
          <span className="badge badge-soft-primary font-monospace fs-7">{code}</span>
        ) : (
          "-"
        );
      },
    },
    {
      header: "IBAN",
      className: "align-middle font-monospace",
      render: (row) => row.iban_no || "-",
    },
    {
      header: "Country",
      className: "align-middle",
      render: (row) => row.country || "-",
    },
    {
      header: "Document",
      className: "align-middle text-center",
      headerClassName: "text-center",
      render: (row) => {
        const doc = row.book_bank_image || row.document || "";
        if (!doc) return <span className="text-muted">-</span>;
        const fileUrl = getFullMediaUrl(doc);
        return (
          <button
            type="button"
            className="btn btn-sm btn-soft-info p-1 px-2 d-inline-flex align-items-center gap-1"
            title="View Document"
            onClick={() =>
              setDocModalData({
                url: fileUrl,
                bankName: row.bank_name || "Bank Document",
                accountNo: row.account_no || row.account_number || "",
                holderName: row.account_holder_name || row.holder_name || "",
              })
            }
          >
            <i className="bx bx-file font-size-14" /> View
          </button>
        );
      },
    },
    {
      header: "Status",
      className: "align-middle text-center",
      headerClassName: "text-center",
      render: () => (
        <span className="badge bg-success-subtle text-success">
          <i className="bx bx-check-circle me-1" /> Active
        </span>
      ),
    },
  ];

  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* Page Title & Actions */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between p-0 bg-transparent">
                    <div className="d-flex align-items-center gap-2">
                      <h2 className="fw-700 mb-0">Bank Details</h2>
                    </div>
                    <div className="page-title-right d-flex align-items-center gap-2">
                      {/* <button
                        type="button"
                        className="btn btn-soft-secondary d-flex align-items-center gap-1"
                        onClick={handleRefresh}
                        disabled={loading}
                      >
                        <i className={`bx bx-refresh font-size-16 ${loading ? "bx-spin" : ""}`} />
                        {loading ? "Checking..." : "Refresh"}
                      </button> */}
                      {!isBankAdded && (
                        <button
                          type="button"
                          className={`btn ${showAddForm ? "btn-outline-secondary" : "btn-primary"} d-flex align-items-center gap-1`}
                          onClick={() => setShowAddForm(!showAddForm)}
                        >
                          <i className={`bx ${showAddForm ? "bx-chevron-up" : "bx-plus"} font-size-16`} />
                          {showAddForm ? "Hide Form" : "Add Bank Details"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Security Notice */}
              {/* <div className="row mb-3">
                <div className="col-12">
                  <div className="alert alert-info alert-dismissible fade show border-0 d-flex align-items-center py-2" role="alert">
                    <i className="bx bx-info-circle fs-4 me-2 text-info flex-shrink-0" />
                    <div className="font-size-13">
                      <strong>Important Notice:</strong> Ensure the account holder name exactly matches your verified KYC identity. Third-party bank accounts are strictly prohibited.
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Add Bank Details Form Card (Only shown if bank details not added yet) */}
              {!isBankAdded && showAddForm && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card border shadow-sm">
                      <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between py-3">
                        <div className="d-flex align-items-center gap-2">
                          <span className="avatar-xs">
                            <span className="avatar-title rounded-circle bg-primary text-white">
                              <i className="bx bx-credit-card font-size-16" />
                            </span>
                          </span>
                          <h5 className="card-title mb-0">Add Bank Details</h5>
                        </div>
                        <button
                          type="button"
                          className="btn-close"
                          aria-label="Close"
                          onClick={() => setShowAddForm(false)}
                        />
                      </div>

                      <div className="card-body p-4">
                        <form onSubmit={handleSubmit} noValidate>
                          <div className="row g-3">
                            {/* Account Holder Name */}
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Account Holder Name <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="bx bx-user" />
                                </span>
                                <input
                                  type="text"
                                  className={`form-control ${errors.account_holder_name ? "is-invalid" : ""}`}
                                  name="account_holder_name"
                                  placeholder="Enter account holder name"
                                  value={formData.account_holder_name}
                                  onChange={handleInputChange}
                                  disabled={submitLoading}
                                />
                              </div>
                              {errors.account_holder_name && (
                                <div className="invalid-feedback d-block">{errors.account_holder_name}</div>
                              )}
                            </div>

                            {/* Bank Name */}
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Bank Name <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="bx bx-buildings" />
                                </span>
                                <input
                                  type="text"
                                  className={`form-control ${errors.bank_name ? "is-invalid" : ""}`}
                                  name="bank_name"
                                  placeholder="Enter bank name (e.g. HDFC Bank, SBI)"
                                  value={formData.bank_name}
                                  onChange={handleInputChange}
                                  disabled={submitLoading}
                                />
                              </div>
                              {errors.bank_name && (
                                <div className="invalid-feedback d-block">{errors.bank_name}</div>
                              )}
                            </div>

                            {/* Account Number */}
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Account Number <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="bx bx-hash" />
                                </span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  className={`form-control ${errors.account_no ? "is-invalid" : ""}`}
                                  name="account_no"
                                  placeholder="Enter account number"
                                  value={formData.account_no}
                                  onChange={handleInputChange}
                                  disabled={submitLoading}
                                />
                              </div>
                              {errors.account_no && (
                                <div className="invalid-feedback d-block">{errors.account_no}</div>
                              )}
                            </div>

                            {/* IFSC / SWIFT Code */}
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                IFSC / SWIFT Code <span className="text-muted fw-normal">(Optional)</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="bx bx-barcode" />
                                </span>
                                <input
                                  type="text"
                                  className="form-control font-monospace"
                                  name="ifsc_swift_code"
                                  placeholder="e.g. SBIN0001234 or CHASUS33"
                                  value={formData.ifsc_swift_code}
                                  onChange={handleInputChange}
                                  disabled={submitLoading}
                                />
                              </div>
                            </div>

                            {/* IBAN Number */}
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                IBAN Number <span className="text-muted fw-normal">(Optional)</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="bx bx-globe" />
                                </span>
                                <input
                                  type="text"
                                  className="form-control font-monospace"
                                  name="iban_no"
                                  placeholder="e.g. GB29NWBK60161331926819"
                                  value={formData.iban_no}
                                  onChange={handleInputChange}
                                  disabled={submitLoading}
                                />
                              </div>
                            </div>

                            {/* Bank Address */}
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Bank Address <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <span className="input-group-text bg-light">
                                  <i className="bx bx-map-pin" />
                                </span>
                                <input
                                  type="text"
                                  className={`form-control ${errors.bank_address ? "is-invalid" : ""}`}
                                  name="bank_address"
                                  placeholder="Enter branch name or full bank address"
                                  value={formData.bank_address}
                                  onChange={handleInputChange}
                                  disabled={submitLoading}
                                />
                              </div>
                              {errors.bank_address && (
                                <div className="invalid-feedback d-block">{errors.bank_address}</div>
                              )}
                            </div>

                            {/* Searchable Country Dropdown */}
                            <div className="col-md-6" ref={countryRef}>
                              <label className="form-label fw-semibold">
                                Country <span className="text-danger">*</span>
                              </label>
                              <div className="position-relative">
                                <button
                                  type="button"
                                  className={`form-select text-start d-flex justify-content-between align-items-center ${errors.country ? "is-invalid" : ""}`}
                                  onClick={() => {
                                    setCountryDropdownOpen(!countryDropdownOpen);
                                    setCountrySearchQuery("");
                                  }}
                                  disabled={submitLoading}
                                >
                                  <span className={formData.country ? "text-dark" : "text-muted"}>
                                    {formData.country || "Select Country"}
                                  </span>
                                </button>

                                {countryDropdownOpen && (
                                  <div
                                    className="dropdown-menu show w-100 p-2 shadow-lg border mt-1"
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      left: 0,
                                      zIndex: 1050,
                                      maxHeight: "260px",
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <div className="input-group input-group-sm mb-2">
                                      <span className="input-group-text bg-light border-end-0">
                                        <i className="bx bx-search text-muted" />
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search country..."
                                        value={countrySearchQuery}
                                        onChange={(e) => setCountrySearchQuery(e.target.value)}
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      {countrySearchQuery && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-light border"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setCountrySearchQuery("");
                                          }}
                                        >
                                          <i className="bx bx-x" />
                                        </button>
                                      )}
                                    </div>

                                    <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                                      {countries.filter((c) =>
                                        c.toLowerCase().includes(countrySearchQuery.toLowerCase())
                                      ).length > 0 ? (
                                        countries
                                          .filter((c) =>
                                            c.toLowerCase().includes(countrySearchQuery.toLowerCase())
                                          )
                                          .map((c, i) => (
                                            <button
                                              key={i}
                                              type="button"
                                              className={`dropdown-item rounded py-1 px-2 d-flex align-items-center justify-content-between ${formData.country === c ? "active" : ""}`}
                                              onClick={() => {
                                                setFormData((prev) => ({ ...prev, country: c }));
                                                if (errors.country) {
                                                  setErrors((prev) => ({ ...prev, country: "" }));
                                                }
                                                setCountryDropdownOpen(false);
                                                setCountrySearchQuery("");
                                              }}
                                            >
                                              <span>{c}</span>
                                              {formData.country === c && <i className="bx bx-check" />}
                                            </button>
                                          ))
                                      ) : (
                                        <div className="text-muted text-center py-2 font-size-12">
                                          No country found
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                              {errors.country && (
                                <div className="invalid-feedback d-block">{errors.country}</div>
                              )}
                            </div>

                            {/* Book Bank Image Upload (Custom, fixes 'No file chosen') */}
                            <div className="col-md-6">
                              <label className="form-label fw-semibold">
                                Passbook / Bank Statement Document <span className="text-danger">*</span>
                              </label>
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
                                className="d-none"
                                onChange={handleFileChange}
                                disabled={submitLoading}
                              />
                              <div className="input-group">
                                <button
                                  type="button"
                                  className="btn btn-primary d-flex align-items-center gap-1"
                                  onClick={() => fileInputRef.current?.click()}
                                  disabled={submitLoading}
                                >
                                  <i className="bx bx-upload" /> Choose File
                                </button>
                                <input
                                  type="text"
                                  readOnly
                                  className={`form-control bg-white cursor-pointer ${errors.book_bank_image ? "is-invalid" : ""}`}
                                  placeholder="No file chosen"
                                  value={bookBankFile ? bookBankFile.name : ""}
                                  onClick={() => fileInputRef.current?.click()}
                                  style={{ cursor: "pointer" }}
                                />
                                {bookBankFile && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={clearFile}
                                    title="Remove file"
                                  >
                                    <i className="bx bx-x font-size-16" />
                                  </button>
                                )}
                              </div>
                              {errors.book_bank_image && (
                                <div className="invalid-feedback d-block">{errors.book_bank_image}</div>
                              )}
                              <small className="text-muted d-block mt-1">
                                Upload passbook, cheque, or statement (JPG, PNG, PDF up to 10MB).
                              </small>

                              {/* File Preview */}
                              {bookBankFile && (
                                <div className="mt-2 p-2 bg-light rounded border d-flex align-items-center justify-content-between">
                                  <div className="d-flex align-items-center gap-2 overflow-hidden">
                                    {filePreview ? (
                                      <img
                                        src={filePreview}
                                        alt="Preview"
                                        style={{ width: "42px", height: "42px", objectFit: "cover", borderRadius: "4px" }}
                                      />
                                    ) : (
                                      <i className="bx bxs-file-pdf font-size-28 text-danger" />
                                    )}
                                    <div className="overflow-hidden">
                                      <span className="fw-semibold font-size-12 d-block text-truncate" style={{ maxWidth: "200px" }}>
                                        {bookBankFile.name}
                                      </span>
                                      <small className="text-muted">
                                        {(bookBankFile.size / (1024 * 1024)).toFixed(2)} MB
                                      </small>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger p-1"
                                    onClick={clearFile}
                                    title="Remove File"
                                  >
                                    <i className="bx bx-trash font-size-14" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                            <button
                              type="submit"
                              className="btn btn-primary px-4 d-flex align-items-center gap-2"
                              disabled={submitLoading}
                            >
                              {submitLoading && <span className="spinner-border spinner-border-sm" />}
                              <i className="bx bx-save" /> Save Bank Details
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Saved Bank Details Cards and Table */}
              <div className="row">
                <div className="col-12">
                  {bankList.length > 0 ? (
                    <>
                      {/* Premium Card Display */}
                      <div className="row mb-4">
                        {bankList.map((item, index) => (
                          <div className="col-xl-4 col-md-6 mb-3" key={index}>
                            <div
                              className="card text-white border-0 shadow-lg position-relative overflow-hidden"
                              style={{
                                background: "linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)",
                                borderRadius: "16px",
                                minHeight: "220px",
                              }}
                            >
                              <div className="card-body p-4 d-flex flex-column justify-content-between position-relative">
                                {/* Card Top */}
                                <div className="d-flex justify-content-between align-items-center">
                                  <div className="d-flex align-items-center gap-2">
                                    <i className="bx bxs-bank font-size-22 text-warning" />
                                    <span className="fw-bold font-size-15 text-uppercase letter-spacing-1">
                                      {item.bank_name || "Bank Account"}
                                    </span>
                                  </div>
                                  <span className="badge bg-success font-size-11 px-2 py-1">
                                    <i className="bx bx-check-circle me-1" /> Active
                                  </span>
                                </div>

                                {/* Chip */}
                                <div className="my-3 d-flex align-items-center justify-content-between">
                                  <svg width="40" height="28" viewBox="0 0 42 30" fill="none">
                                    <rect width="42" height="30" rx="4" fill="#E0A938" />
                                    <path d="M0 11H42M0 19H42M14 0V30M28 0V30" stroke="#7A5600" strokeWidth="1" />
                                  </svg>
                                  <span className="badge bg-white-50 text-white font-size-11 fw-normal">
                                    {item.country || "Verified"}
                                  </span>
                                </div>

                                {/* Account Number */}
                                <div>
                                  <div className="d-flex align-items-center justify-content-between">
                                    <span className="font-monospace fs-5 fw-bold letter-spacing-2">
                                      {maskAccountNumber(item.account_no || item.account_number)}
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-light p-1 border-0"
                                      title="Copy Account Number"
                                      onClick={() =>
                                        copyText(item.account_no || item.account_number, "Account Number")
                                      }
                                    >
                                      <i className="bx bx-copy font-size-16" />
                                    </button>
                                  </div>
                                </div>

                                {/* Card Bottom */}
                                <div className="d-flex justify-content-between align-items-end mt-3 pt-2 border-top border-white-50">
                                  <div>
                                    <small className="text-white-50 font-size-11 d-block text-uppercase">
                                      Account Holder
                                    </small>
                                    <span className="fw-semibold font-size-13 text-capitalize">
                                      {item.account_holder_name || item.holder_name || "-"}
                                    </span>
                                  </div>
                                  <div className="text-end">
                                    <small className="text-white-50 font-size-11 d-block text-uppercase">
                                      IFSC / SWIFT
                                    </small>
                                    <span className="font-monospace fw-bold font-size-12">
                                      {item.ifsc_swift_code || item.ifsc_code || "-"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Saved Accounts Table */}
                      <div className="card">
                        <div className="card-header bg-transparent border-bottom d-flex align-items-center justify-content-between py-3">
                          <h5 className="card-title mb-0">Saved Bank Accounts</h5>
                          <span className="badge bg-primary-subtle text-primary font-size-12">
                            {bankList.length} {bankList.length === 1 ? "Account" : "Accounts"} Registered
                          </span>
                        </div>
                        <div className="card-body">
                          <CommonTable
                            columns={tableColumns}
                            data={bankList}
                            disablePagination={bankList.length <= 10}
                          />
                        </div>
                      </div>
                    </>
                  ) : !isBankAdded && !showAddForm ? (
                    <div className="card text-center py-5">
                      <div className="card-body">
                        <div className="avatar-lg mx-auto mb-4">
                          <span className="avatar-title rounded-circle bg-light text-primary font-size-32">
                            <i className="bx bxs-bank" />
                          </span>
                        </div>
                        <h4 className="fw-semibold">No Bank Details Added Yet</h4>
                        <p className="text-muted mb-4 max-w-md mx-auto" style={{ maxWidth: "480px" }}>
                          You have not linked any bank accounts to your profile. Add your bank details to enable fund withdrawals.
                        </p>
                        <button
                          type="button"
                          className="btn btn-primary px-4 py-2"
                          onClick={() => setShowAddForm(true)}
                        >
                          <i className="bx bx-plus me-1" /> Add Bank Details Now
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>

      {/* Document View Modal */}
      {docModalData && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(4px)",
            zIndex: 1055,
          }}
          onClick={() => setDocModalData(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "12px", overflow: "hidden" }}>
              <div className="modal-header bg-light py-3">
                <div className="d-flex align-items-center gap-2">
                  <span className="avatar-xs">
                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-16">
                      <i className="bx bx-file" />
                    </span>
                  </span>
                  <div>
                    <h5 className="modal-title font-size-16 mb-0">Bank Document</h5>
                    <small className="text-muted">
                      {docModalData.bankName} {docModalData.accountNo ? `(•••• ${String(docModalData.accountNo).slice(-4)})` : ""}
                    </small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => setDocModalData(null)}
                />
              </div>

              <div
                className="modal-body text-center p-3"
                style={{
                  backgroundColor: "#f8f9fa",
                  maxHeight: "75vh",
                  overflowY: "auto",
                }}
              >
                {docModalData.url?.toLowerCase().endsWith(".pdf") ? (
                  <div style={{ height: "65vh", width: "100%" }}>
                    <iframe
                      src={docModalData.url}
                      title="Bank Document PDF"
                      width="100%"
                      height="100%"
                      style={{ border: "none", borderRadius: "8px" }}
                    />
                  </div>
                ) : (
                  <div className="d-flex align-items-center justify-content-center" style={{ minHeight: "300px" }}>
                    <img
                      src={docModalData.url}
                      alt="Bank Statement / Passbook"
                      className="img-fluid rounded border shadow-sm"
                      style={{
                        maxHeight: "68vh",
                        maxWidth: "100%",
                        objectFit: "contain",
                        backgroundColor: "#fff",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://placehold.co/600x400?text=Document+Preview+Unavailable";
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="modal-footer bg-light py-2 px-3 d-flex justify-content-between align-items-center">
                <div className="font-size-12 text-muted">
                  {docModalData.holderName && (
                    <span>
                      Account Holder: <strong>{docModalData.holderName}</strong>
                    </span>
                  )}
                </div>
                <div className="d-flex gap-2">
                  <a
                    href={docModalData.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary d-flex align-items-center gap-1"
                  >
                    <i className="bx bx-external-link" /> Open in New Tab
                  </a>
                  <button
                    type="button"
                    className="btn btn-secondary px-3"
                    onClick={() => setDocModalData(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
